import { HttpClient } from '@angular/common/http';
import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { SwPush } from '@angular/service-worker';
import { Store } from '@ngrx/store';
import { Observable, combineLatest, firstValueFrom, map, of } from 'rxjs';
import { filter, take } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { AuthService } from './auth.service';
import { I18nService } from './i18n.service';
import { NotificationService } from './notifications.service';

const LINKED_KEY = 'pushLinkedToAccount';
const BANNER_DISMISSED_KEY = 'chatPushBannerDismissed';
// Même clé que le bouton de la barre du haut : la personne a coupé les notifications de cet appareil
const PUSH_OPT_OUT_KEY = 'pushOptOut';

// Rattache l'abonnement push de l'appareil au compte connecté, pour recevoir messages et appels appli fermée.
// À la déconnexion, l'appareil est détaché : un téléphone partagé ne reçoit pas les messages d'un autre.
@Injectable({
  providedIn: 'root'
})
export class ChatPushService {

  // Connecté, notifications possibles sur ce navigateur, mais pas encore activées
  readonly canActivate$: Observable<boolean>;

  private lastSync = '';

  constructor(
    private swPush: SwPush,
    private httpClient: HttpClient,
    private authService: AuthService,
    private i18n: I18nService,
    private notificationService: NotificationService,
    private store: Store<any>,
    @Inject(PLATFORM_ID) platformId: Object,
  ) {
    if (!isPlatformBrowser(platformId) || !this.swPush.isEnabled) {
      this.canActivate$ = of(false);
      return;
    }
    this.canActivate$ = combineLatest([this.authService.currentUser$, this.swPush.subscription]).pipe(
      map(([user, subscription]) => !!user && !subscription && this.permission !== 'denied' && !this.read(PUSH_OPT_OUT_KEY) && !this.read(BANNER_DISMISSED_KEY)),
    );
    combineLatest([this.authService.currentUser$, this.swPush.subscription, this.i18n.language$])
      .subscribe(([user, subscription, language]) => this.sync(user?._id ?? null, subscription, language));
  }

  // Doit être appelé depuis un clic : le navigateur l'exige pour demander l'autorisation
  async activate(): Promise<void> {
    const serverPublicKey: string = await firstValueFrom(
      this.store.select(state => state.SettingsStateFromModule?.settings?.[0]?.publicKeyNotification).pipe(filter(key => !!key), take(1)),
    );
    const subscription = await this.swPush.requestSubscription({ serverPublicKey });
    await this.notificationService.addSubscription(subscription);
    this.write(PUSH_OPT_OUT_KEY, null);
  }

  dismissBanner(): void {
    this.write(BANNER_DISMISSED_KEY, '1');
  }

  get permission(): NotificationPermission {
    return typeof Notification === 'undefined' ? 'denied' : Notification.permission;
  }

  private sync(userId: string | null, subscription: PushSubscription | null, language: string): void {
    const state = [userId, subscription?.endpoint, language].join('|');
    if (!subscription || state === this.lastSync) {
      return;
    }
    this.lastSync = state;
    if (userId) {
      this.httpClient.post(environment.host + '/chat/push-subscription', { sub: subscription, language }).subscribe({
        next: () => this.write(LINKED_KEY, '1'),
        error: err => console.error('Could not link push subscription to account:', err),
      });
    } else if (this.read(LINKED_KEY)) {
      this.httpClient.post(environment.host + '/products/subscription/unlink', { endpoint: subscription.endpoint }).subscribe({
        next: () => this.write(LINKED_KEY, null),
        error: err => console.error('Could not unlink push subscription:', err),
      });
    }
  }

  private read(key: string): string | null {
    try {
      return localStorage.getItem(key);
    } catch {
      return null;
    }
  }

  private write(key: string, value: string | null): void {
    try {
      value === null ? localStorage.removeItem(key) : localStorage.setItem(key, value);
    } catch {
      // stockage indisponible (navigation privée)
    }
  }
}
